export {
  ALARM_PRIORITY,
  AlarmConfigState,
  AlarmMode,
  CameraLinkType,
  PRIORITY,
  type AlarmContactEntity,
  type AlarmContactSiteAssignment,
  type AlarmContactType,
  type AlarmProviderType,
  type AlarmSiteLabel,
  type AlarmZone,
  type Broadcast,
  type CallhomeAlarmConfig,
  type CallhomeAlarmState,
  type CallhomeCameraLink,
  type CreateAlarmContactEntity,
  type DisarmMethod,
  type Point,
  type Profile,
  type SipConfig,
} from '@solinkcorp/api-types';

import {
  type AiTagId,
  type CHCameraId,
  type CloudId,
  type CustomerId,
  type DeviceId,
  type ResellerId,
  type ScheduleId,
  type SiteId,
} from '@solinkcorp/api-types';

export type OwnerType = 'solink' | 'cloud' | 'reseller' | 'customer' | 'user' | 'device';

export type ReportType = 'aggregation' | 'filter';

export interface Translatable {
  /**
   * Keys are locales (like "en-US").
   */
  [locale: string]: string;
}

export enum ScheduledActivityOwner {
  CUSTOMER = 'CUSTOMER',
  DEVICE = 'DEVICE',
}

export enum ScheduledActivityRuleType {
  CRON = 'CRON',
}

export enum ScheduledActivityType {
  SCHEDULED_INSPECTION = 'scheduled-inspection',
  SCHEDULED_AI_CHAT = 'scheduled-ai-chat',
  ALARM_EXCEPTION = 'scheduled-activity',
}

// https://github.com/SolinkCorp/call-home-server/blob/master/src/database/entities/scheduled-activity.entity.ts

export interface ScheduledActivityMetadata {
  /**
   * legacy field, no longer being supported, used for backwards compatibility
   * refer to aiChatAnswerStates for the new structured output
   * @deprecated
   */
  aiChatAnswerFormat?: string | null;
  aiChatPrompt?: string;
  aiChatAnswerStates?: { name: string; description: string }[];
  /**
   *  user selected camera group that is used to reinitialize the scheduled activity form's state
   * @deprecated
   */
  aiChatCameraGroupId?: string;
  /**
   * camera name for lambda to annotate images
   * https://github.com/SolinkCorp/event-standard/pull/835#pullrequestreview-1871342945
   * @deprecated
   */
  cameraGroupName?: string;
  weeklyScheduleId?: 'aiInferredOffHours' | null;
  inSchedulePeriods?: boolean;
  /**
   * Template specific fields
   * */
  isTemplate?: boolean;
  templateCategoryKey?: string;
  templateThumbnail?: string;

  // Timestamp when the exception was created or modified
  lastWriteTime?: number;
}

export interface ScheduledActivityEntity {
  id: ScheduleId;
  cloudId?: CloudId;
  resellerId?: ResellerId;
  customerId?: CustomerId;
  deviceId?: DeviceId;
  siteId?: SiteId;
  owner?: ScheduledActivityOwner;
  cameraIds: CHCameraId[];
  title: string;
  description?: string;
  reason?: string;

  // Unix timestamp
  // When the schedule should start taking effect, 0 means start immediately
  startsAt: number;

  // Unix timestamp
  // When the schedule should end, -1 means never end
  endsAt: number;

  // The duration of the scheduled event
  durationSecs: number;

  // Type of Scheduled Activity, if we wanted to support RRULE we could make it customizable
  ruleType: ScheduledActivityRuleType;

  // Standard cron string describing the recurring schedule
  rule: string;

  // The number of times the activity should re-occur. Set to -1 to re-occur indefinitely.
  reoccurringCount: number;

  // Are alarms Enabled
  isAlarmsEnabled: boolean;

  // Data to be attached to the event
  metadata?: ScheduledActivityMetadata;

  // type of scheduled activity - note: this is not currently supported by callhome
  // (we're instead temporarily using the reason to store the type for scheduled inspections
  // to avoid having to update callhome in the short time span available to develop a PoC)
  activityType?: ScheduledActivityType;
}

export type ReportQueryType = 'raw' | 'template' | 'compound';

export interface CallHomeReportTemplate {
  id: string;
  name: Translatable;
  description: Translatable;
  reportType: ReportType;
  queryType: ReportQueryType;
  query: unknown; // QueryDslQueryContainer | SearchRequest;
  ownerType: OwnerType;
  reportMetadata?: {
    categories?: readonly string[];
  };
  cloudId: string;
  resellerId?: string | null;
  customerId?: string | null;
  userId?: string | null;
  mergedReport?: {
    inheritType: string;
    isVisible?: boolean;
    queryParams?: any;
    reportSettings: any;
  };
  reports?: CallHomeReport[];
  userNotifications?: unknown[];
}

export type ReportInheritType = 'all' | 'assigned';

export interface CallHomeReport {
  id: string;
  reportType?: ReportType | null;
  ownerType: OwnerType;
  inheritType?: ReportInheritType | null;
  queryParams?: unknown | null;
  reportSettings?: unknown | null;
  isVisible?: boolean | null;
  hasDigest?: boolean | null;
  reportTemplateId: string;
  rootReportId?: string | null;
  cloudId: string;
  resellerId?: string | null;
  customerId?: string | null;
  userId?: string | null;
}

export interface AiNotification {
  id?: string;
  cloudId: string;
  resellerId: string;
  customerId: string;
  reportTemplateId: string;
  aiChatPrompt: string;
  aiChatAnswerFormat: string | null;
  aiChatTimeTarget: string;
  aiChatTimestampOffset: number;
  metadata?: {
    title?: string;
    aiChatAnswerStates?: { name: string; description: string }[];
  };
}

export interface AiTag {
  id?: AiTagId;
  cloudId: string;
  resellerId: string;
  customerId: string;
  name: string;
}
